import { useEffect, useState } from "react";

import {
  ListContainer,
  ListContext,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
  Page,
  PageRoute,
  SearchBar,
  buildPath,
  buildTemplatePath,
  pn,
} from "@compass/core";

import { ListFooter } from "components";
import { DistrictListDto, DistrictListResource, ListFilterBase, Permission } from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { DistrictListPageHeader } from "./DistrictListPageHeader";
import { DistrictTable } from "./DistrictTable";

const routeName = "districts";

export const districtListPageRoute: PageRoute = {
  handle: "Districts",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const DistrictListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.MatchUtility;

  const [listItems, setListItems] = useState<DistrictListDto[]>([]);
  const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
    sortObjects: [{ property: pn<DistrictListDto>("name"), isDesc: false }],
  });
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<DistrictListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.districts.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.districts.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);

    ApiService.districts.getListResource().then((response) => {
      setResource(response);
    });
  }, []);

  return (
    <>
      <Page header={<DistrictListPageHeader rootPermission={ROOT_PERMISSION} handle={districtListPageRoute.handle} />}>
        <ListContainer
          fullHeight
          header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
          tableContainer={
            <DistrictTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              districts={listItems}
              setDistricts={setListItems}
              resource={resource}
              searchFocused={searchFocused}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
